import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import {
  buildUnifiedSelectionShapeResizeCommitItems,
  computeUnifiedSelectionScaledResizePreview,
} from '@/lib/transforms/unified-selection-resize'
import { MIN_DIMENSION } from '../constants/panel-limits'
import type { DimensionField, PositionField } from '../types/panel-fields'
import { isPanelShapeGeometryBlocked, shapesByIdFromManager } from './selection-protection'
import { multiSelectionCanvasUnionAabb } from './selection-bounds'

/**
 * Moves every non-blocked selected shape by the same delta so the selection's union min corner
 * lands on `spreadPoints`. Returns the shapes that actually moved (for the batch commit) and the
 * manager's shapes after the move.
 */
export function applyMultiSelectPanelMove(params: {
  mgr: ShapeManagerRef
  activeObjects: ShapeData[]
  field: PositionField
  spreadPoints: number
}): { batchItems: BatchShapeTransformItem[]; freshShapes: ShapeData[] } {
  const { mgr, activeObjects, field, spreadPoints } = params
  const union = multiSelectionCanvasUnionAabb(activeObjects)
  const minVal = field === 'x' ? union.minX : union.minY
  const delta = spreadPoints - minVal
  const EPS_POS = 1e-4
  const batchItems: BatchShapeTransformItem[] = []

  const shapesByIdForCommit = shapesByIdFromManager(mgr)

  for (const obj of activeObjects) {
    const latest = shapesByIdForCommit.get(obj.id) ?? obj

    if (isPanelShapeGeometryBlocked(latest, shapesByIdForCommit)) continue

    const newVal = (field === 'x' ? obj.x : obj.y) + delta

    mgr.updateShape(obj.id, { [field]: newVal })
  }

  const fresh = mgr.getShapes()

  for (const obj of activeObjects) {
    const shape = fresh.find((s) => s.id === obj.id)

    if (!shape) continue
    const before = { x: obj.x, y: obj.y }

    if (Math.abs(before.x - shape.x) <= EPS_POS && Math.abs(before.y - shape.y) <= EPS_POS) {
      continue
    }

    batchItems.push({
      shapeId: obj.id,
      before,
      after: { x: shape.x, y: shape.y },
      shape,
    })
  }

  return { batchItems, freshShapes: fresh }
}

/** Target selection W/H for a multi-select commit: both sides follow the typed value while linked. */
export function computeMultiSelectTargetSize(params: {
  current: { width: number; height: number }
  field: DimensionField
  points: number
  linked: boolean
}): { width: number; height: number } {
  const { current, field, points, linked } = params

  if (linked) {
    return { width: points, height: points }
  }

  return {
    width: field === 'width' ? points : current.width,
    height: field === 'height' ? points : current.height,
  }
}

/**
 * Resizes a multi-selection to the target W/H: the manager's shapes-only chrome resize when it
 * applies, otherwise a scaled preview applied per non-blocked shape. Returns the batch items to
 * commit, or `null` when no scaled preview could be computed (nothing changed).
 */
export function applyMultiSelectPanelResize(params: {
  mgr: ShapeManagerRef
  activeObjects: ShapeData[]
  field: DimensionField
  targetWidth: number
  targetHeight: number
  dimensionsLinked: boolean
}): BatchShapeTransformItem[] | null {
  const { mgr, activeObjects, field, targetWidth, targetHeight, dimensionsLinked } = params

  let batchItems: BatchShapeTransformItem[] = []
  let usedShapesOnlyChromeResize = false

  const canUseShapesOnlyChromeResize =
    activeObjects.length >= 2 && Boolean(mgr?.applyMultiSelectPanelDimensionCommit)

  if (canUseShapesOnlyChromeResize) {
    const items = mgr!.applyMultiSelectPanelDimensionCommit!({
      targetChromeWidth: targetWidth,
      targetChromeHeight: targetHeight,
      field,
      dimensionsLinked,
    })

    if (items !== null) {
      usedShapesOnlyChromeResize = true
      batchItems = items
    }
  }

  if (!usedShapesOnlyChromeResize) {
    const preview = computeUnifiedSelectionScaledResizePreview({
      items: activeObjects.map((shape) => ({
        id: shape.id,
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
      })),
      targetWidth,
      targetHeight,
      minDimension: MIN_DIMENSION,
    })

    if (!preview) {
      return null
    }

    if (mgr) {
      const shapesByIdForCommit = shapesByIdFromManager(mgr)

      for (const obj of activeObjects) {
        const next = preview[obj.id]

        if (!next) continue
        const latest = shapesByIdForCommit.get(obj.id) ?? obj

        if (isPanelShapeGeometryBlocked(latest, shapesByIdForCommit)) continue

        mgr.updateShape(obj.id, next)
      }
    }

    batchItems =
      mgr && activeObjects.length > 0
        ? buildUnifiedSelectionShapeResizeCommitItems({
            initials: Object.fromEntries(
              activeObjects.map((shape) => [
                shape.id,
                { x: shape.x, y: shape.y, width: shape.width, height: shape.height },
              ])
            ),
            shapes: mgr.getShapes(),
          })
        : []
  }

  return batchItems
}
